import React, { createContext, useContext, useReducer, ReactNode, useEffect, useMemo, useCallback } from 'react';

import { rootReducer } from './reducers/rootReducer';

import { Beer, BeerContextValue } from './types';

import { Action } from './actions/ActionInterface';

import { useStorageService } from '../services/storage/StorageServiceProvider';

import { BeerDataProvider } from './providers/BeerDataProvider';



// Create the context with null as default value

export const BeerContext = createContext<BeerContextValue | null>(null);



// Provider props interface

interface BeerProviderProps {

  children: ReactNode;

  initialBeers?: Beer[];

}



/**

 * Provider component for beer state

 * Manages the application state using the rootReducer and provides it through context

 * Follows the Dependency Inversion Principle by injecting the storage service

 */

export function BeerProvider({ children, initialBeers = [] }: BeerProviderProps): React.ReactElement {

  // Get storage service from context (dependency injection)

  const storageService = useStorageService();

  

  // Initialize state with rootReducer

  const [state, dispatch] = useReducer(rootReducer, undefined, () => {

    // console.log('[BeerContext] Initializing state');

    // Initialize with default state from rootReducer

    const initialState = rootReducer(undefined, { type: '' } as Action);

    

    // Override with initialBeers if provided

    if (initialBeers.length > 0) {

      initialState.beers = initialBeers;

    }

    

    // Load user interactions from storage

    try {

      // Load favorites

      const favoritesJson = localStorage.getItem('favorites');

      if (favoritesJson) {

        initialState.userInteractions.favorites = JSON.parse(favoritesJson);

      }

      

      // Load tasting list

      const tastingListJson = localStorage.getItem('tastingList');

      if (tastingListJson) {

        initialState.userInteractions.tastingList = JSON.parse(tastingListJson);

      }

      

      // Load tried list

      const triedListJson = localStorage.getItem('triedList');

      if (triedListJson) {

        initialState.userInteractions.triedList = JSON.parse(triedListJson);

      }

      

      // Load ratings

      const ratingsJson = localStorage.getItem('ratings');

      if (ratingsJson) {

        initialState.userInteractions.ratings = JSON.parse(ratingsJson);

      }

      

      // Load notes

      const notesJson = localStorage.getItem('notes');

      if (notesJson) {

        initialState.userInteractions.notes = JSON.parse(notesJson);

      }

    } catch (error) {

      console.error('Failed to load user interactions from storage:', error);

    }

    

    // console.log('[BeerContext] Initial state created:', initialState);

    return initialState;

  });

  

  // Log state changes

  useEffect(() => {

    // console.log('[BeerContext] Current state:', state);

  }, [state]);



  // Create action creators

  const setSearchTerm = useCallback((term: string) => {

    dispatch({ type: 'SET_SEARCH_TERM', payload: term, execute: (s) => ({

      ...s,

      filters: { ...s.filters, searchTerm: term }

    })})

  }, [dispatch]);



  const setTypeFilter = useCallback((filter: string) => {

    dispatch({ type: 'SET_TYPE_FILTER', payload: filter, execute: (s) => ({

      ...s,

      filters: { ...s.filters, typeFilter: filter }

    })})

  }, [dispatch]);



  const setDrinkTypeFilter = useCallback((filter: string) => {

    dispatch({ type: 'SET_DRINK_TYPE_FILTER', payload: filter, execute: (s) => ({

      ...s,

      filters: { ...s.filters, drinkTypeFilter: filter }

    })})

  }, [dispatch]);



  const setAvailabilityFilter = useCallback((filter: string) => {

    dispatch({ type: 'SET_AVAILABILITY_FILTER', payload: filter, execute: (s) => ({

      ...s,

      filters: { ...s.filters, availabilityFilter: filter }

    })})

  }, [dispatch]);



  const setBreweryFilter = useCallback((filter: string) => {

    dispatch({ type: 'SET_BREWERY_FILTER', payload: filter, execute: (s) => ({

      ...s,

      filters: { ...s.filters, breweryFilter: filter }

    })})

  }, [dispatch]);



  const toggleFavorite = useCallback((beerId: string) => {

    dispatch({ type: 'TOGGLE_FAVORITE', payload: beerId, execute: (s) => {

      // Ensure userInteractions and favorites exist

      const userInteractions = s.userInteractions || {};

      const favorites = Array.isArray(userInteractions.favorites) ? [...userInteractions.favorites] : [];

      let tastingList = Array.isArray(userInteractions.tastingList) ? [...userInteractions.tastingList] : [];

      

      const index = favorites.indexOf(beerId);

      if (index === -1) {

        favorites.push(beerId);

        

        // If adding to favorites, remove from tasting list (beer has been tried)

        const tastingIndex = tastingList.indexOf(beerId);

        if (tastingIndex !== -1) {

          tastingList.splice(tastingIndex, 1);

        }

      } else {

        favorites.splice(index, 1);

      }

      

      // Save to localStorage for persistence

      try {

        localStorage.setItem('favorites', JSON.stringify(favorites));

        localStorage.setItem('tastingList', JSON.stringify(tastingList));

      } catch (error) {

        console.error('Failed to save to localStorage:', error);

      }

      

      return {

        ...s,

        userInteractions: { ...userInteractions, favorites, tastingList }

      };

    }})

  }, [dispatch]);



  const updateRating = useCallback((beerId: string, rating: number) => {

    dispatch({ type: 'UPDATE_RATING', payload: { beerId, rating }, execute: (s) => {

      // Ensure userInteractions and ratings exist

      const userInteractions = s.userInteractions || {};

      const ratings = userInteractions.ratings || {};

      let tastingList = Array.isArray(userInteractions.tastingList) ? [...userInteractions.tastingList] : [];

      

      // Create new ratings object with the updated rating

      const updatedRatings = { ...ratings, [beerId]: rating };

      

      // If rating is greater than 0, remove from tasting list (beer has been tried)

      if (rating > 0) {

        const tastingIndex = tastingList.indexOf(beerId);

        if (tastingIndex !== -1) {

          tastingList.splice(tastingIndex, 1);

        }

      }

      

      // Save to localStorage for persistence

      try {

        localStorage.setItem('ratings', JSON.stringify(updatedRatings));

        localStorage.setItem('tastingList', JSON.stringify(tastingList));

      } catch (error) {

        console.error('Failed to save to localStorage:', error);

      }

      

      return {

        ...s,

        userInteractions: { 

          ...userInteractions, 

          ratings: updatedRatings,

          tastingList

        }

      };

    }})

  }, [dispatch]);



  const updateNotes = useCallback((beerId: string, notes: string) => {

    dispatch({ type: 'UPDATE_NOTES', payload: { beerId, notes }, execute: (s) => {

      // Ensure userInteractions and notes exist

      const userInteractions = s.userInteractions || {};

      const currentNotes = userInteractions.notes || {};

      let tastingList = Array.isArray(userInteractions.tastingList) ? [...userInteractions.tastingList] : [];

      

      // Create new notes object with the updated note

      const updatedNotes = { ...currentNotes, [beerId]: notes };

      

      // If notes are not empty, remove from tasting list (beer has been tried)

      if (notes && notes.trim() !== '') {

        const tastingIndex = tastingList.indexOf(beerId);

        if (tastingIndex !== -1) {

          tastingList.splice(tastingIndex, 1);

        }

      }

      

      // Save to localStorage for persistence

      try {

        localStorage.setItem('notes', JSON.stringify(updatedNotes));

        localStorage.setItem('tastingList', JSON.stringify(tastingList));

      } catch (error) {

        console.error('Failed to save to localStorage:', error);

      }

      

      return {

        ...s,

        userInteractions: { 

          ...userInteractions, 

          notes: updatedNotes,

          tastingList

        }

      };

    }})

  }, [dispatch]);



  const toggleTastingList = useCallback((beerId: string) => {

    dispatch({ type: 'TOGGLE_TASTING_LIST', payload: beerId, execute: (s) => {

      // Ensure userInteractions and tastingList exist

      const userInteractions = s.userInteractions || {};

      const tastingList = Array.isArray(userInteractions.tastingList) ? [...userInteractions.tastingList] : [];

      

      const index = tastingList.indexOf(beerId);

      

      // Check if beer has already been tried (has rating, notes, or is favorited)

      const hasRating = userInteractions.ratings && userInteractions.ratings[beerId] && userInteractions.ratings[beerId] > 0;

      const hasNotes = userInteractions.notes && userInteractions.notes[beerId] && userInteractions.notes[beerId].trim() !== '';

      const isFavorited = userInteractions.favorites && userInteractions.favorites.includes(beerId);

      const isAlreadyTried = hasRating || hasNotes || isFavorited;

      

      if (index === -1) {

        // Only add to tasting list if not already tried

        if (!isAlreadyTried) {

          tastingList.push(beerId);

        } else {

          console.log('Cannot add to tasting list - beer has already been tried/rated/favorited');

          return s; // Return unchanged state

        }

      } else {

        // Always allow removal from tasting list

        tastingList.splice(index, 1);

      }

      

      // Save to localStorage for persistence

      try {

        localStorage.setItem('tastingList', JSON.stringify(tastingList));

      } catch (error) {

        console.error('Failed to save tasting list to localStorage:', error);

      }

      

      return {

        ...s,

        userInteractions: { ...userInteractions, tastingList }

      };

    }})

  }, [dispatch]);



  const toggleTried = useCallback((beerId: string) => {

    dispatch({ type: 'TOGGLE_TRIED', payload: beerId, execute: (s) => {

      // Ensure userInteractions and triedList exist

      const userInteractions = s.userInteractions || {};

      const triedList = Array.isArray(userInteractions.triedList) ? [...userInteractions.triedList] : [];

      

      const index = triedList.indexOf(beerId);

      if (index === -1) {

        triedList.push(beerId);

      } else {

        triedList.splice(index, 1);

      }

      

      // Save to localStorage for persistence

      try {

        localStorage.setItem('triedList', JSON.stringify(triedList));

      } catch (error) {

        console.error('Failed to save tried list to localStorage:', error);

      }

      

      return {

        ...s,

        userInteractions: { ...userInteractions, triedList }

      };

    }})

  }, [dispatch]);



  const addTagFilter = useCallback((tag: string) => {

    dispatch({ type: 'ADD_TAG_FILTER', payload: tag, execute: (s) => {

      // Defensive check to ensure s.filters exists

      if (!s.filters) {

        // Initialize filters if it doesn't exist

        return {

          ...s,

          filters: {

            searchTerm: '',

            typeFilter: '',

            breweryFilter: '',

            drinkTypeFilter: '',

            availabilityFilter: '',

            tagFilters: [tag],

            showOnlyFavorites: false,

            showOnlyAvailable: false,

            filterByFavorites: false

          }

        };

      }

      

      // Defensive check for tagFilters

      const currentTagFilters = s.filters.tagFilters || [];

      

      // Check if tag already exists

      if (currentTagFilters.includes(tag)) return s;

      

      return {

        ...s,

        filters: { 

          ...s.filters, 

          tagFilters: [...currentTagFilters, tag] 

        }

      };

    }})

  }, [dispatch]);



  // Compute filtered beers

  const filteredBeers = useMemo(() => {

    // console.log('[BeerContext] Calculating filtered beers, current beers:', state.beers);

    const { filters, userInteractions } = state;

    

    const result = state.beers.filter(beer => {

      // Search term filter

      if (filters.searchTerm && !beer.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) && 

          !beer.brewery.toLowerCase().includes(filters.searchTerm.toLowerCase())) {

        return false;

      }

      

      // Type filter

      if (filters.typeFilter && beer.style !== filters.typeFilter) {

        return false;

      }

      

      // Brewery filter

      if (filters.breweryFilter && beer.brewery !== filters.breweryFilter) {

        return false;

      }

      

      // Drink type filter

      if (filters.drinkTypeFilter && beer.type !== filters.drinkTypeFilter) {

        return false;

      }

      

      // Availability filter

      if (filters.availabilityFilter && beer.availability !== filters.availabilityFilter) {

        return false;

      }

      

      // Tag filters

      if (filters.tagFilters.length > 0) {

        const hasAllTags = filters.tagFilters.every(tag => 

          beer.tags && beer.tags.includes(tag)

        );

        

        if (!hasAllTags) {

          return false;

        }

      }

      

      // Show only favorites filter

      if (filters.showOnlyFavorites && !userInteractions.favorites.includes(beer.id)) {

        return false;

      }

      

      // Show only available filter

      if (filters.showOnlyAvailable && beer.availability !== 'available') {

        return false;

      }

      

      return true;

    });

    

    // console.log('[BeerContext] Filtered beers result:', result);

    return result;

  }, [state]);



  // Compute empty message

  const emptyMessage = useMemo(() => {

    const { filters } = state;

    const activeTab = state.ui.activeTab;

    if (activeTab === '1') {
      if (filters.showOnlyFavorites && state.userInteractions.tastingList.length === 0) {
        return "You haven't added any beers to your tasting list yet. Just click on a beer to add it.";
      }

      const hasAnyTriedBeers =
        (state.userInteractions.triedList?.length ?? 0) > 0 ||
        state.userInteractions.favorites.length > 0 ||
        Object.values(state.userInteractions.ratings).some((rating) => rating > 0) ||
        Object.values(state.userInteractions.notes).some((note) => typeof note === 'string' && note.trim() !== '');

      if (!filters.showOnlyFavorites && !hasAnyTriedBeers) {
        return 'Your Tried Beers list is empty. Rate or favourite beers and they will appear here.';
      }
    }

    

    if (filters.searchTerm) {

      return `No beers found matching "${filters.searchTerm}".`;

    }

    

    if (filters.tagFilters.length > 0) {

      return `No beers found with the selected tags.`;

    }

    

    if (filters.typeFilter || filters.breweryFilter || filters.availabilityFilter) {

      return "No beers match the current filters.";

    }

    

    return "No beers available.";

  }, [state]);



  // Memoize context value to prevent unnecessary re-renders

  const contextValue = useMemo<BeerContextValue>(() => {

    // console.log('[BeerContext] Creating context value with filteredBeers:', filteredBeers);

    return {

    // Core state and dispatch

    state,

    dispatch,

    

    // Direct state access

    beers: state.beers,

    loading: state.loading,

    error: state.error,

    filters: state.filters,

    userInteractions: state.userInteractions,

    activeTab: state.ui.activeTab,

    filteredBeers,

    emptyMessage,

    

    // Actions

    setSearchTerm,

    setTypeFilter,

    setDrinkTypeFilter,

    setAvailabilityFilter,

    setBreweryFilter,

    toggleFavorite,

    updateRating,

    updateNotes,

    toggleTastingList,

    toggleTried,

    addTagFilter

  };

  }, [state, dispatch, filteredBeers, emptyMessage, setSearchTerm, setTypeFilter, 

      setDrinkTypeFilter, setAvailabilityFilter, setBreweryFilter, toggleFavorite, 

      updateRating, updateNotes, toggleTastingList, toggleTried, addTagFilter]);



  // Periodically flush storage updates

  useEffect(() => {

    const interval = setInterval(() => {

      storageService.flushUpdates();

    }, 5000);



    return () => {

      clearInterval(interval);

      storageService.flushUpdates();

    };

  }, [storageService]);



  return (

    <BeerContext.Provider value={contextValue}>

      <BeerDataProvider dispatch={dispatch} state={state}>

        {children}

      </BeerDataProvider>

    </BeerContext.Provider>

  );

}



/**

 * Hook to use beer context - this is kept for backward compatibility

 * New code should use the more specific hooks like useFilters, useUserInteractions, etc.

 */

export function useBeer(): BeerContextValue {

  const context = useContext(BeerContext);

  

  if (context === null) {

    throw new Error('useBeer must be used within a BeerProvider');

  }

  

  return context;

}

